// Up-front image warming for smooth presenting.
//
// V2 decks render slides one at a time (slide-stage clones the active slide
// from an inert <template>), and slide art is mostly CSS background-image, so
// each image is only fetched when its slide is first shown — a visible stutter
// mid-presentation. Warming every image URL up front populates the browser's
// HTTP cache (image responses are content-addressed + immutable), so each
// slide paints instantly when reached. Pure, DOM-light helpers so they're
// reusable from the share page and unit-testable.

/**
 * Collect every fetchable image URL referenced by a rendered deck's HTML.
 *
 * Decks reference images two ways: CSS `background-image:url(...)` art layers
 * and `<img src>` (brand logos). We keep only http(s) and root-relative URLs
 * (covers `/api/images/<id>`) and skip inline `data:` URIs (already inline —
 * nothing to fetch). Returns a de-duplicated list.
 */
export function collectImageUrls(html: string): string[] {
  if (!html) return [];
  const urls = new Set<string>();
  const add = (raw: string | undefined | null): void => {
    if (!raw) return;
    const u = raw.trim().replace(/^['"]|['"]$/g, '').trim();
    if (!u || u.startsWith('data:')) return;
    if (u.startsWith('http://') || u.startsWith('https://') || u.startsWith('/')) urls.add(u);
  };

  // CSS url(...) — background-image / background shorthand / mask etc.
  const cssUrl = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;
  let m: RegExpExecArray | null;
  while ((m = cssUrl.exec(html)) !== null) add(m[2]);

  // <img ... src="...">
  const imgSrc = /<img\b[^>]*?\ssrc\s*=\s*(['"])([^'"]+)\1/gi;
  while ((m = imgSrc.exec(html)) !== null) add(m[2]);

  return [...urls];
}

/** Options for {@link warmImages}. */
export interface WarmOptions {
  /** Overall cap (ms) after which the promise resolves regardless. */
  timeoutMs?: number;
  /** Called as each image settles (and once at the start with done=0). */
  onProgress?: (done: number, total: number) => void;
}

/**
 * Warm the browser HTTP cache for a set of image URLs via `new Image()`.
 *
 * Resolves when every image settles (load OR error) or the overall timeout
 * elapses — a slow or 404 image can never block the caller. Reports progress
 * as each image settles. Never rejects.
 */
export function warmImages(urls: string[], opts: WarmOptions = {}): Promise<void> {
  const { timeoutMs = 6000, onProgress } = opts;
  const total = urls.length;
  if (total === 0) {
    onProgress?.(0, 0);
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    let done = 0;
    let finished = false;
    const finish = (): void => {
      if (finished) return;
      finished = true;
      clearTimeout(timer);
      resolve();
    };
    const tick = (): void => {
      done += 1;
      onProgress?.(done, total);
      if (done >= total) finish();
    };
    const timer = setTimeout(finish, timeoutMs);
    onProgress?.(0, total);
    for (const url of urls) {
      const img = new Image();
      img.onload = tick;
      img.onerror = tick;
      img.decoding = 'async';
      img.src = url;
    }
  });
}
